import { z } from 'zod';
import { personBondSchema, personStatusSchema, sexSchema } from './people.js';

/**
 * Importação de pessoas por planilha (D25). A igreja chega com o cadastro em
 * Excel; se cadastrar 80 pessoas à mão for necessário, o SaaS morre na porta.
 */
export const IMPORT_FIELDS = [
  'fullName',
  'displayName',
  'phone',
  'email',
  'birthDate',
  'sex',
  'congregation',
  'office',
  'bond',
  'status',
  'ministrySince',
  'addressLine',
  'city',
  'state',
  'notes',
  'guardianName',
  'guardianPhone',
  'ministries',
  'tags',
] as const;

export type ImportField = (typeof IMPORT_FIELDS)[number];

export const importFieldSchema = z.enum(IMPORT_FIELDS);

export const IMPORT_FIELD_LABEL: Record<ImportField, string> = {
  fullName: 'Nome completo',
  displayName: 'Como é chamado',
  phone: 'WhatsApp / telefone',
  email: 'E-mail',
  birthDate: 'Data de nascimento',
  sex: 'Sexo',
  congregation: 'Congregação',
  office: 'Cargo eclesiástico',
  bond: 'Vínculo',
  status: 'Situação',
  ministrySince: 'Serve desde',
  addressLine: 'Endereço',
  city: 'Cidade',
  state: 'Estado',
  notes: 'Observações',
  guardianName: 'Responsável legal',
  guardianPhone: 'Telefone do responsável',
  ministries: 'Departamentos',
  tags: 'Etiquetas',
};

/**
 * Cabeçalhos que aparecem nas planilhas reais das igrejas. Servem para adivinhar
 * o mapeamento e deixar a pessoa só conferir, em vez de montar tudo do zero.
 */
export const IMPORT_FIELD_ALIASES: Record<ImportField, readonly string[]> = {
  fullName: ['nome', 'nome completo', 'membro', 'pessoa', 'nome do membro', 'irmao', 'irmão'],
  displayName: ['apelido', 'como e chamado', 'nome social', 'tratamento'],
  phone: ['telefone', 'celular', 'whatsapp', 'whats', 'fone', 'contato', 'zap'],
  email: ['email', 'e-mail', 'correio eletronico'],
  birthDate: ['nascimento', 'data de nascimento', 'aniversario', 'aniversário', 'dt nascimento'],
  sex: ['sexo', 'genero', 'gênero'],
  congregation: ['congregacao', 'congregação', 'campus', 'igreja', 'local'],
  office: ['cargo', 'funcao eclesiastica', 'função eclesiástica', 'ministerio eclesiastico'],
  bond: ['vinculo', 'vínculo', 'tipo'],
  status: ['situacao', 'situação', 'status', 'ativo'],
  ministrySince: ['serve desde', 'inicio no ministerio', 'membro desde', 'batismo'],
  addressLine: ['endereco', 'endereço', 'rua', 'logradouro'],
  city: ['cidade', 'municipio', 'município'],
  state: ['estado', 'uf'],
  notes: ['observacao', 'observação', 'observacoes', 'obs'],
  guardianName: ['responsavel', 'responsável', 'responsavel legal', 'pai', 'mae', 'mãe'],
  guardianPhone: ['telefone do responsavel', 'celular do responsavel', 'contato do responsavel'],
  ministries: ['departamento', 'departamentos', 'ministerio', 'ministério', 'ministerios', 'equipe'],
  tags: ['etiqueta', 'etiquetas', 'tag', 'tags', 'grupo'],
};

export const importMappingSchema = z.record(z.string(), importFieldSchema.nullable());
export type ImportMapping = z.infer<typeof importMappingSchema>;

export const importPeopleSchema = z.object({
  /** Conteúdo bruto do arquivo, ou o texto colado da planilha. */
  content: z.string().min(1, 'Cole a planilha ou envie o arquivo').max(5_000_000),
  /** Coluna do arquivo → campo do cadastro. Quando ausente, é adivinhado. */
  mapping: importMappingSchema.optional(),
  defaults: z
    .object({
      congregationId: z.string().uuid().nullish(),
      bond: personBondSchema.default('VOLUNTEER'),
      status: personStatusSchema.default('ACTIVE'),
      sex: sexSchema.default('UNDISCLOSED'),
    })
    .default({}),
  /** Atualiza quem já existe (mesmo telefone ou e-mail) em vez de pular. */
  updateExisting: z.boolean().default(false),
  /** Cria departamentos e etiquetas que ainda não existem. */
  createMissingReferences: z.boolean().default(true),
});
export type ImportPeopleInput = z.infer<typeof importPeopleSchema>;

export const importIssueSchema = z.object({
  line: z.number().int(),
  column: z.string().nullable(),
  message: z.string(),
  /** `error` impede a linha; `warning` não impede, mas a pessoa precisa saber. */
  severity: z.enum(['error', 'warning']),
});
export type ImportIssue = z.infer<typeof importIssueSchema>;

export const importPreviewRowSchema = z.object({
  line: z.number().int(),
  fullName: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  action: z.enum(['create', 'update', 'skip']),
  issues: z.array(importIssueSchema),
});
export type ImportPreviewRow = z.infer<typeof importPreviewRowSchema>;

export const importReportSchema = z.object({
  dryRun: z.boolean(),
  detectedMapping: importMappingSchema,
  unmappedColumns: z.array(z.string()),
  totals: z.object({
    lines: z.number().int(),
    created: z.number().int(),
    updated: z.number().int(),
    skipped: z.number().int(),
    failed: z.number().int(),
  }),
  rows: z.array(importPreviewRowSchema),
  issues: z.array(importIssueSchema),
  createdReferences: z.object({
    ministries: z.array(z.string()),
    tags: z.array(z.string()),
    offices: z.array(z.string()),
  }),
});
export type ImportReport = z.infer<typeof importReportSchema>;

/** Modelo de planilha oferecido para download na tela de importação. */
export const IMPORT_TEMPLATE_HEADERS = [
  'Nome completo',
  'WhatsApp',
  'E-mail',
  'Data de nascimento',
  'Sexo',
  'Congregação',
  'Cargo',
  'Departamentos',
  'Etiquetas',
  'Responsável legal',
  'Telefone do responsável',
] as const;

export const IMPORT_TEMPLATE_EXAMPLE = [
  [
    'Ana Paula Ferreira',
    '+5511988887777',
    'ana@exemplo.com',
    '1990-04-12',
    'Feminino',
    'Sede',
    'Membro',
    'Louvor e adoração; Mídia e tecnologia',
    'Voz',
    '',
    '',
  ],
  [
    'Pedro Henrique Souza',
    '+5511977776666',
    '',
    '2009-08-30',
    'Masculino',
    'Sede',
    'Congregado',
    'Louvor e adoração',
    'Bateria',
    'Marta Souza',
    '+5511966665555',
  ],
] as const;
